import { Link, useParams } from "wouter";
import { useGetEconomatEcoleDetail } from "@/lib/backoffice-hooks";
import { formatDateShort } from "@/lib/format";
import EconomatShell from "@/components/layout/EconomatShell";
import { Card } from "@/components/ui/card";
import { useState } from "react";
import { ArrowLeft, Loader2, Download, Eye } from "lucide-react";
import { downloadRecuPdf } from "@/lib/downloadRecu";
import RecuPreviewDialog from "@/components/RecuPreviewDialog";

function fcfa(n: number) { return new Intl.NumberFormat("fr-FR").format(n) + " FCFA"; }
const STATUS_LABEL: Record<string, string> = {
  SUCCESS: "Encaissé", PROCESSING: "En cours", PENDING: "En attente",
  FAILED: "Échoué", CANCELLED: "Annulé", REFUNDED: "Remboursé",
};

export default function EconomatEcoleDetail() {
  const params = useParams<{ ecoleId: string }>();
  const [apercu, setApercu] = useState<{ id: string; reference: string } | null>(null);
  const { data, isLoading, error } = useGetEconomatEcoleDetail(params.ecoleId);

  if (isLoading) return <EconomatShell><div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div></EconomatShell>;
  if (error || !data) return (
    <EconomatShell>
      <div className="text-center py-16 text-slate-600">
        <p>École introuvable dans votre diocèse, ou endpoint <code>/api/economat/ecoles/{params.ecoleId}</code> non disponible.</p>
        <Link href="/economat/ecoles" className="text-navy underline text-sm">← Retour à la liste</Link>
      </div>
    </EconomatShell>
  );

  return (
    <EconomatShell>
      <div className="mb-5">
        <Link href="/economat/ecoles" className="text-xs text-slate-500 hover:text-navy inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Retour aux écoles
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">École · {data.communeNom ?? "Non renseigné"}</div>
            <h1 className="text-2xl font-bold text-navy">{data.denomination}</h1>
            <div className="text-sm text-slate-600 mt-1">
              {(data.typeEnseignement ?? "").replace(/_/g, " ") || "Type non renseigné"} ·{" "}
              <span className={`text-xs px-2 py-0.5 rounded ${data.actif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                {data.actif ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Kpi label="Total encaissé" value={fcfa(data.totalEncaisseXof)} tone="success" />
        <Kpi label="Paiements encaissés" value={String(data.nbPaiementsSuccess)} />
        <Kpi label="En attente" value={String(data.nbPaiementsPending)} tone={data.nbPaiementsPending > 0 ? "warning" : undefined} />
        <Kpi label="Élèves" value={String(data.nbEleves)} />
        <Kpi label="Motifs actifs" value={String(data.nbMotifsActifs)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <BreakdownCard title="Par motif" rows={data.parMotif} />
        <BreakdownCard title="Par mode de paiement" rows={data.parMethode} />
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-navy">Derniers paiements ({data.recentPaiements.length})</h2>
        </div>
        {data.recentPaiements.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Aucun paiement.</div>}
        {data.recentPaiements.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-white">
              <tr>
                <th className="text-left px-4 py-2">Référence</th>
                <th className="text-left">Élève</th>
                <th className="text-left">Motif</th>
                <th className="text-right">Montant</th>
                <th className="text-left pl-3">Mode</th>
                <th className="text-left">Statut</th>
                <th className="text-left">Date</th>
                <th className="text-center pr-4">Reçu</th>
              </tr>
            </thead>
            <tbody>
              {data.recentPaiements.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-paiement-${p.reference}`}>
                  <td className="px-4 py-2 font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{p.reference}</td>
                  <td>{p.eleveNom}</td>
                  <td className="text-slate-600">{p.motifLibelle}</td>
                  <td className="text-right font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(p.montantXof)}</td>
                  <td className="pl-3 text-xs text-slate-600">{String(p.methode).replace(/_/g, " ")}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded ${p.status === "SUCCESS" ? "bg-emerald-100 text-emerald-700" : p.status === "FAILED" || p.status === "CANCELLED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </td>
                  <td className="text-xs text-slate-500">{formatDateShort(p.paidAt ?? p.createdAt)}</td>
                  <td className="text-center pr-4">
                    {p.status === "SUCCESS" && (
                      <div className="inline-flex items-center gap-0.5">
                        <button onClick={() => setApercu({ id: p.id, reference: p.reference })} title="Aperçu du reçu" className="text-slate-500 hover:text-navy p-1" data-testid={`button-apercu-${p.reference}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => downloadRecuPdf(p.id, p.reference)} title="Télécharger le reçu PDF" className="text-slate-500 hover:text-navy p-1" data-testid={`button-pdf-${p.reference}`}>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <RecuPreviewDialog
        open={apercu !== null}
        onOpenChange={(o) => !o && setApercu(null)}
        source={{ kind: "auth", paiementId: apercu?.id ?? "" }}
        reference={apercu?.reference}
      />
    </EconomatShell>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: "success" | "warning" }) {
  const color = tone === "success" ? "text-emerald-700" : tone === "warning" ? "text-amber-700" : "text-navy";
  return (
    <Card className="p-4 border border-slate-200 shadow-none">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 font-mono ${color}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{value}</div>
    </Card>
  );
}

function BreakdownCard({ title, rows }: { title: string; rows: Array<{ key: string; label: string; count: number; totalXof: number }> }) {
  const total = rows.reduce((a, r) => a + r.totalXof, 0);
  return (
    <Card className="border border-slate-200 shadow-none overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50"><h3 className="text-sm font-semibold text-navy">{title}</h3></div>
      {rows.length === 0 && <div className="p-4 text-center text-sm text-slate-500">Aucune donnée.</div>}
      <div className="divide-y divide-slate-100">
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.totalXof / total) * 100) : 0;
          return (
            <div key={r.key} className="px-4 py-2.5">
              <div className="flex justify-between items-baseline text-sm">
                <span className="font-medium text-slate-700 truncate pr-2">{r.label.replace(/_/g, " ")}</span>
                <span className="font-mono text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(r.totalXof)}</span>
              </div>
              <div className="flex justify-between text-[11px] text-slate-500 mt-0.5">
                <span>{r.count} paiement{r.count > 1 ? "s" : ""}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-1 bg-slate-100 rounded overflow-hidden mt-1">
                <div className="h-full bg-navy" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
